import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { WheelGestures } from "../logic/wheel_gestures"

// Pages en diapositives, à poser sur un ancêtre des pages rendues par
// DepozenUi::ContinuousPageComponent. Mode d'emploi : docs/frontend/continuous_pages.md.
//
// Une seule page est visible à la fois : le défilement s'arrête donc de lui-même à sa fin (ou à
// son début). Un NOUVEAU geste au-delà — molette, pavé tactile, balayage, flèches, Espace,
// Page suivante, ou le lien du bas de page — fait glisser la voisine à sa place (View Transition
// API). Le geste qui amène en bout de page ne bascule jamais : on s'arrête, puis on repart.
//
// Ce que fait encore le contrôleur :
//   - page courante : adresse et titre de l'onglet réécrits, lien du sommaire actif, événement
//     `continuous-pages:change` ;
//   - préchargement : les deux voisines de la page courante se chargent, cachées, sans attendre ;
//     une voisine pas encore arrivée glisse sous forme de squelette, remplacé sur place ;
//   - en-tête commun à plusieurs pages (docs/frontend/continuous_pages.md, « En-tête commun ») : un
//     élément marqué `data-continuous-pages-persist` ne glisse pas avec sa page, il reste en place
//     pendant la bascule ; un en-tête collant (cible `sticky`) porte `data-stuck` dès qu'il colle, et la
//     page suivante arrive défilée d'autant s'il collait sur celle qu'on quitte ;
//   - clic sur une pastille de cet en-tête (action `pill`) : la visite se fait comme avant, mais l'en-tête ne bouge
//     pas — un squelette remplace à l'instant ce qui le suit, la pastille cliquée devient active, et la page demandée
//     arrive avec l'en-tête dans le même état (titre déployé, à mi-chemin ou barre collée, cf. `carriedHeader`).
//
// Trois pièges traités :
//   - l'aperçu du cache Turbo (`html[data-turbo-preview]`) montre l'instantané de la page,
//     voisines comprises. S'y caler réécrivait l'adresse vers la mauvaise page : pendant un
//     aperçu, on ne touche à rien ;
//   - l'inertie du pavé tactile : le geste qui fait basculer continue d'émettre des `wheel`
//     pendant une seconde ou plus. On les avale jusqu'à la fin du geste, sans quoi la page
//     d'arrivée défilerait toute seule. Les `wheel` poussés au-delà du bord sont annulés dès le
//     premier : Chrome ne laisse annuler la suite d'un geste que si son début l'a été ;
//   - le geste en cours pendant la bascule : la superposition de la View Transition reçoit les
//     `wheel`, d'où l'écoute sur la fenêtre plutôt que sur le contrôleur.

// Le découpage des `wheel` en gestes, et ce qui fait d'un geste en bout de page un geste délibéré (repos, silence,
// distance) : ../logic/wheel_gestures.js.
// Balayage tactile minimal (px).
const SWIPE_TRAVEL = 56
// Tolérance (px) du bout de page (défilement fractionnaire).
const EDGE_TOLERANCE = 2
// Durée maximale (ms) pendant laquelle on avale l'inertie du geste qui a fait basculer.
const INERTIA_WINDOW = 1200

const KEYS = { ArrowDown: "next", PageDown: "next", ArrowUp: "previous", PageUp: "previous" }
const EDITABLE = "input, textarea, select, [contenteditable=''], [contenteditable='true']"
// Ce qui garde les touches pour lui : Espace actionne un bouton, les flèches parcourent un menu.
const SPACE_TAKERS = "button, summary, [role='button'], [role='checkbox'], [role='switch'], [role='menuitem']"
const ARROW_TAKERS = "[role='menu'], [role='listbox'], [role='tablist'], [role='grid'], [role='tree'], [role='slider'], [role='spinbutton'], [role='combobox'], [role='radiogroup']"
// Une surcouche posée dans la coquille (panneau latéral, boîte de dialogue) défile et se pilote au clavier pour elle-même :
// ni sa molette, ni ses touches, ni ses balayages ne font basculer la page, même quand la coquille la contient (sous
// 992 px, c'est la coquille qui défile).
const OVERLAY = "[role='dialog'], [data-continuous-pages-ignore]"
// Un élément qui reste en place d'une page à l'autre pendant la bascule (cf. `persistentPairs`).
const PERSIST = "[data-continuous-pages-persist]"
// Un en-tête collant (`position: sticky; top: -1px`) dépasse d'un pixel du bord haut de ce qui défile dès qu'il colle,
// et plus d'un demi-pixel (défilement fractionnaire) le distingue de sa place naturelle. L'observateur guette ce
// pixel sans écouter `scroll` : collé, l'en-tête n'est plus visible qu'à 1 − 1/hauteur (99,2 % pour 120 px, 99,8 % pour
// 600 px), d'où cent seuils serrés entre 95 et 100 % : un réveil à chaque demi-pixel de recouvrement, quelle que soit
// la hauteur. Ce n'est qu'un réveil : l'état se mesure sur la géométrie (`syncSticky`).
const STUCK_TOLERANCE = 0.5
const STICKY_THRESHOLDS = Array.from({ length: 101 }, (_, index) => Math.min(1, (950 + index / 2) / 1000))
// Pour coller un en-tête à l'arrivée d'une page, on défile de deux pixels de plus que sa place naturelle : un pixel
// de recouvrement (`top: -1px`), et de quoi passer la tolérance ci-dessus.
const STICK_OVERSHOOT = 2
// Écart (px) toléré entre deux éléments qui « restent en place » d'une page à l'autre, ou avec le bord de ce qui défile.
const SAME_SPOT = 1.5
// L'en-tête d'une page que l'on quitte par une pastille (cf. `pill`) : le contrôleur de la page d'arrivée est un autre
// objet, ce module les relie. Périmé après ce délai (ms), ou si la visite aboutit ailleurs que sur la page demandée.
const HEADER_CARRY_TTL = 20000
let carriedHeader = null

export default class extends Controller {
  static targets = ["page", "link", "frame", "scroller", "sticky", "skeleton"]

  static values = {
    // Clé de la page demandée, posée par le serveur, puis tenue à jour (l'instantané Turbo d'un
    // retour arrière rouvre la dernière page vue).
    current: String,
    // Marge laissée au-dessus d'une page quand on s'y place, si du contenu la précède.
    topGap: { type: Number, default: 8 },
    prefetch: { type: Boolean, default: true },
    activeClass: { type: String, default: "is-active" },
  }

  initialize() {
    this.onWheel = (event) => this.wheel(event)
    this.gestures = new WheelGestures()
    // L'inertie du pavé tactile défile encore la page : on note quand elle a bougé pour la dernière fois.
    this.onScroll = (event) => {
      if (event.target === this.scroller || (event.target === document && this.scroller === document.scrollingElement)) {
        this.gestures.scrolled(event.timeStamp)
      }
    }
    this.onKeydown = (event) => this.keydown(event)
    this.onTouchStart = (event) => this.touchStart(event)
    this.onTouchMove = (event) => this.touchMove(event)
    this.onTouchEnd = () => this.touchEnd()
    this.onTouchCancel = () => { this.touch = null }
    this.onBeforeFrameRender = (event) => this.beforeFrameRender(event)
    this.onFrameMissing = (event) => this.frameFailed(event)
    // L'instantané du cache garde la page d'où l'on venait, sans nom de transition ni état « collé ».
    this.onBeforeCache = () => {
      this.cancelPending()
      this.clearPersisted()
      for (const element of this.stickyTargets) element.removeAttribute("data-stuck")
    }
    this.stickyObserver = new IntersectionObserver((entries) => this.syncSticky(entries.map((entry) => entry.target)), {
      threshold: STICKY_THRESHOLDS,
    })
    // Visite échouée : on reste sur place, les gestes reprennent. Frame en échec : on y va par
    // une visite ordinaire.
    this.onFetchError = (event) => {
      this.frameFailed(event)
      this.leaving = false
      carriedHeader = null
      this.dropSkeleton()
    }
  }

  connect() {
    this.titleSuffix = document.title.split(" | ").slice(1).join(" | ")
    window.addEventListener("wheel", this.onWheel, { passive: false, capture: true })
    document.addEventListener("scroll", this.onScroll, { passive: true, capture: true })
    document.addEventListener("keydown", this.onKeydown)
    this.element.addEventListener("touchstart", this.onTouchStart, { passive: true })
    this.element.addEventListener("touchmove", this.onTouchMove, { passive: true })
    this.element.addEventListener("touchend", this.onTouchEnd)
    this.element.addEventListener("touchcancel", this.onTouchCancel)
    this.element.addEventListener("turbo:before-frame-render", this.onBeforeFrameRender)
    this.element.addEventListener("turbo:frame-missing", this.onFrameMissing)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    document.addEventListener("turbo:fetch-request-error", this.onFetchError)

    // Un instantané du cache pris pendant l'attente d'une pastille garde le squelette : on le retire.
    this.dropSkeleton()
    this.highlight(this.currentValue)
    // Stimulus ne rappelle pas `stickyTargetConnected` pour une cible déjà connue quand le contrôleur se rebranche.
    for (const element of this.stickyTargets) this.stickyObserver.observe(element)
    const carry = this.carriedHeaderFor()
    if (this.previewing) {
      // L'aperçu du cache (la page demandée, déjà vue) arrive sous l'en-tête laissé tel quel, pas titre déployé.
      const shown = this.pageTargets.find((target) => !target.hidden)
      if (carry && shown) this.keepHeader(shown, carry)
      return
    }

    const page = this.pageTargets.find((target) => target.dataset.key === this.currentValue) ??
      this.pageTargets.find((target) => !target.hidden)
    if (!page) return

    this.show(page)
    if (carry) {
      carriedHeader = null
      this.keepHeader(page, carry)
    } else if (!window.location.hash) {
      this.placeTop(page)
    }
    this.syncSticky()
    if (this.prefetchValue) this.prefetchAround(page)
  }

  disconnect() {
    window.removeEventListener("wheel", this.onWheel, { capture: true })
    document.removeEventListener("scroll", this.onScroll, { capture: true })
    document.removeEventListener("keydown", this.onKeydown)
    this.element.removeEventListener("touchstart", this.onTouchStart)
    this.element.removeEventListener("touchmove", this.onTouchMove)
    this.element.removeEventListener("touchend", this.onTouchEnd)
    this.element.removeEventListener("touchcancel", this.onTouchCancel)
    this.element.removeEventListener("turbo:before-frame-render", this.onBeforeFrameRender)
    this.element.removeEventListener("turbo:frame-missing", this.onFrameMissing)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    document.removeEventListener("turbo:fetch-request-error", this.onFetchError)
    this.stickyObserver.disconnect()
    this.clearPersisted()
  }

  // Un en-tête collant arrive (page chargée plus tard) ou part : l'observateur le suit.
  stickyTargetConnected(element) {
    this.stickyObserver.observe(element)
  }

  stickyTargetDisconnected(element) {
    this.stickyObserver.unobserve(element)
    element.removeAttribute("data-stuck")
  }

  // Une voisine arrive cachée (le composant le décide) ; par sûreté, toute page autre que la
  // courante l'est aussi — celles d'un instantané d'avant ce fonctionnement, par exemple.
  pageTargetConnected(page) {
    if (this.previewing || !this.page || page === this.page || page.hidden) return
    if (this.pending && page.parentElement === this.pending.frame) return

    page.hidden = true
  }

  // Ce qui défile : la cible `scroller` si elle défile vraiment (selon la disposition), sinon
  // l'élément du contrôleur, sinon le document (application native : c'est la WebView qui défile).
  get scroller() {
    if (this.hasScrollerTarget && getComputedStyle(this.scrollerTarget).overflowY !== "visible") return this.scrollerTarget
    if (getComputedStyle(this.element).overflowY !== "visible") return this.element
    return this.documentScroller
  }

  get documentScroller() {
    return document.scrollingElement ?? document.documentElement
  }

  get previewing() {
    return document.documentElement.hasAttribute("data-turbo-preview")
  }

  get busy() {
    return Boolean(this.sliding || this.pending)
  }

  // ── Gestes ─────────────────────────────────────────────────────────────────────────────────

  wheel(event) {
    // Ctrl + molette : zoom (c'est aussi le pincement du pavé tactile).
    if (event.ctrlKey || event.defaultPrevented) return
    if (this.inOverlay(event.target)) return
    const inside = this.element.contains(event.target)
    if (!inside && !this.busy && !this.swallowUntil) return
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return

    const delta = this.wheelDelta(event)
    if (!delta) return

    const fresh = this.gestures.track(delta, event.timeStamp)
    if (this.busy) {
      event.preventDefault()
      return
    }
    if (this.swallowUntil) {
      if (!fresh && event.timeStamp < this.swallowUntil) {
        event.preventDefault()
        return
      }
      this.swallowUntil = null
    }
    if (!inside || this.previewing || this.leaving) return

    // Le sommaire, sur desktop, défile dans sa propre colonne.
    if (!this.scroller.contains(event.target)) return

    const direction = delta > 0 ? "next" : "previous"
    if (fresh) this.gestures.arm(this.atEdge(direction))
    if (!this.atEdge(direction)) return

    // Un bloc qui défile lui-même (exemple de code, tableau) garde la main tant qu'il peut, et
    // le geste lui reste acquis.
    if (this.scrollsInside(event.target, delta)) {
      this.gestures.disarm()
      return
    }

    // Rien à défiler de ce côté : on coupe la propagation au parent et l'élastique, et le reste
    // du geste reste annulable (cf. l'inertie, plus haut).
    event.preventDefault()
    if (!this.gestures.armed || !this.neighbourOf(this.page, direction)) return
    if (!this.gestures.push(delta)) return

    this.slide(direction)
  }

  wheelDelta(event) {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * this.scroller.clientHeight
    return event.deltaY
  }

  keydown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
    const direction = event.key === " " ? (event.shiftKey ? "previous" : "next") : KEYS[event.key]
    if (!direction || !this.ownsKey(event.target, event.key)) return

    if (this.busy) {
      event.preventDefault()
      return
    }
    // Touche maintenue : elle défile jusqu'au bout, puis s'arrête comme le reste.
    if (event.repeat || this.previewing || this.leaving || !this.atEdge(direction)) return
    if (!this.neighbourOf(this.page, direction)) return

    event.preventDefault()
    this.slide(direction)
  }

  inOverlay(target) {
    return target instanceof Element && Boolean(target.closest(OVERLAY))
  }

  ownsKey(target, key) {
    if (!(target instanceof Element)) return false
    const free = target === document.body || target === document.documentElement
    if (!free && !this.scroller.contains(target)) return false
    if (this.inOverlay(target) || target.closest(EDITABLE) || target.closest(ARROW_TAKERS)) return false
    return !(key === " " && target.closest(SPACE_TAKERS))
  }

  // Balayage : décidé au lever du doigt, pour que le doigt qui glisse encore ne fasse pas défiler
  // la page d'arrivée. Il faut avoir posé le doigt en bout de page.
  touchStart(event) {
    if (event.touches.length !== 1 || !this.scroller.contains(event.target) || this.inOverlay(event.target)) {
      this.touch = null
      return
    }

    const { clientX, clientY } = event.touches[0]
    this.touch = {
      target: event.target, startX: clientX, startY: clientY, x: clientX, y: clientY,
      next: this.atEdge("next"), previous: this.atEdge("previous"),
    }
  }

  touchMove(event) {
    if (!this.touch || event.touches.length !== 1) return

    this.touch.x = event.touches[0].clientX
    this.touch.y = event.touches[0].clientY
  }

  touchEnd() {
    const touch = this.touch
    this.touch = null
    if (!touch || this.busy || this.previewing || this.leaving) return

    // > 0 : le doigt monte, on veut descendre.
    const delta = touch.startY - touch.y
    if (Math.abs(delta) < SWIPE_TRAVEL || Math.abs(delta) < Math.abs(touch.startX - touch.x) * 1.5) return

    const direction = delta > 0 ? "next" : "previous"
    if (!touch[direction] || !this.atEdge(direction) || this.scrollsInside(touch.target, delta)) return

    this.slide(direction)
  }

  // Lien « Page suivante » du bas de page : on glisse au lieu de recharger. Sans voisine (rien
  // derrière ce lien côté contrôleur), le lien fait une visite ordinaire.
  next(event) {
    if (this.isModifiedClick(event) || this.busy || this.previewing || this.leaving) return
    if (!this.neighbourOf(this.page, "next")) return

    event.preventDefault()
    this.slide("next")
  }

  // ── Bascule ────────────────────────────────────────────────────────────────────────────────

  atEdge(direction) {
    const scroller = this.scroller
    if (direction === "previous") return scroller.scrollTop <= EDGE_TOLERANCE

    return scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - EDGE_TOLERANCE
  }

  // Un ancêtre de `target` (sous le conteneur) qui peut encore défiler dans ce sens.
  scrollsInside(target, delta) {
    const scroller = this.scroller
    for (let node = target instanceof Element ? target : target?.parentElement; node && node !== scroller; node = node.parentElement) {
      if (node.scrollHeight <= node.clientHeight + 1) continue
      if (!/(auto|scroll)/.test(getComputedStyle(node).overflowY)) continue
      if (delta > 0 ? node.scrollTop + node.clientHeight < node.scrollHeight - 1 : node.scrollTop > 0) return true
    }
    return false
  }

  // La voisine de `page` : chargée (`{ page }`), en attente dans sa frame (`{ frame }`), ou rien
  // (première ou dernière page). Les pages se suivent dans l'ordre du document.
  neighbourOf(page, direction) {
    if (!page) return null

    const pages = this.pageTargets
    const index = pages.indexOf(page)
    if (index < 0) return null

    const loaded = pages[index + (direction === "next" ? 1 : -1)]
    if (loaded) return { page: loaded }

    const sibling = direction === "next" ? page.nextElementSibling : page.previousElementSibling
    return sibling && this.frameTargets.includes(sibling) ? { frame: sibling } : null
  }

  async slide(direction) {
    if (this.busy || this.previewing || this.leaving) return

    const neighbour = this.neighbourOf(this.page, direction)
    if (!neighbour) return

    this.sliding = true
    // Ce qui colle sur la page qu'on quitte colle aussi sur celle qui arrive (cf. `place`).
    const carry = this.stuckKeys()
    try {
      await this.transition(direction, () => {
        if (neighbour.page) {
          this.select(neighbour.page)
          this.place(neighbour.page, direction, carry)
        } else {
          this.awaitFrame(neighbour.frame, direction, carry)
        }
      }, neighbour.page)
    } finally {
      this.sliding = false
      this.swallowUntil = performance.now() + INERTIA_WINDOW
    }
  }

  // La page qu'on quitte part d'un côté, la voisine arrive de l'autre (cf. _continuous_pages.scss).
  // Seul le conteneur qui défile est nommé, et le temps de la bascule seulement : le sommaire et
  // l'en-tête restent en place. Ce que la page a de commun avec la voisine (`arrival`, absente quand
  // on glisse vers un squelette) reste en place aussi : cf. `persist`. Sans View Transition,
  // mouvement réduit, onglet caché ou document qui défile (application native) : bascule sèche.
  async transition(direction, update, arrival) {
    const scroller = this.scroller
    const animate = typeof document.startViewTransition === "function" && !document.hidden &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches && scroller !== this.documentScroller
    if (!animate) {
      update()
      return
    }

    const root = document.documentElement
    scroller.classList.add("continuous-pages-sliding")
    root.dataset.continuousPagesSlide = direction
    this.persist(this.page, arrival, direction)
    try {
      await document.startViewTransition(() => {
        update()
        this.settlePersisted()
      }).finished
    } catch {
      // Mise à jour en échec : il n'y a rien à animer, l'état reste celui d'avant.
    } finally {
      scroller.classList.remove("continuous-pages-sliding")
      delete root.dataset.continuousPagesSlide
      this.clearPersisted()
    }
  }

  // Les éléments de la page qu'on quitte marqués `data-continuous-pages-persist` qui ont leur pareil (même
  // valeur) dans la page d'arrivée ne glissent pas avec elle : le temps de la bascule, chaque paire reçoit un
  // nom de transition à elle. La View Transition la capture à part et ne fait que fondre l'ancien dans le nouveau
  // (la pastille active d'un en-tête, son filet), à sa place, ou en glissant de l'une à l'autre quand elles ne
  // sont pas au même endroit (une barre collée d'un côté, pas de l'autre). Un élément sans pareil glisse avec sa page.
  // Les deux sont nommés d'avance : la page d'arrivée est cachée, un élément caché n'est pas capturé, et les
  // deux ne sont jamais visibles ensemble. Mais jamais hors de la partie visible de ce qui défile : la View
  // Transition ne rogne pas ce que l'ancêtre cache, un élément défilé hors champ se peindrait par-dessus ce qui
  // entoure ce qui défile. On ne nomme donc que ce qui est à l'écran au départ, et vers la précédente (qui arrive
  // en bas, défilée) que ce qui colle ; la paire est revérifiée une fois la page d'arrivée en place
  // (`settlePersisted`).
  persist(from, to, direction) {
    this.persisted = []
    for (const [departure, arrival] of this.persistentPairs(from, to)) {
      if (direction === "previous" && !this.stickyTargets.includes(departure)) continue

      const rect = departure.getBoundingClientRect()
      if (!this.inView(rect)) continue

      const index = this.persisted.length
      for (const element of [departure, arrival]) {
        element.style.setProperty("view-transition-name", `continuous-pages-persist-${index}`)
        // La classe d'une paire : la feuille de style la reconnaît (cf. _continuous_pages.scss).
        element.style.setProperty("view-transition-class", "continuous-pages-persist")
      }
      this.persisted.push({ departure, arrival, rect })
    }
  }

  // La mise à jour faite, la page d'arrivée est à sa place : ce qui reste en place doit être à l'écran des deux côtés, à la
  // même taille (sinon la capture s'étire de l'un à l'autre). Sinon l'élément d'arrivée perd son nom et glisse avec sa
  // page ; celui qu'on quitte, déjà capturé, s'efface sur place.
  settlePersisted() {
    for (const { arrival, rect } of this.persisted ?? []) {
      const next = arrival.getBoundingClientRect()
      if (this.inView(next) && ["width", "height"].every((side) => Math.abs(next[side] - rect[side]) <= SAME_SPOT)) continue

      this.unname(arrival)
    }
  }

  // Dans la partie visible de ce qui défile (à un pixel de recouvrement près : un en-tête collant dépasse du bord).
  inView(rect) {
    const scroller = this.scroller
    const top = this.scrollportTop
    const bottom = scroller === this.documentScroller ? window.innerHeight : top + scroller.clientHeight
    return rect.height > 0 && rect.top >= top - SAME_SPOT && rect.bottom <= bottom + SAME_SPOT
  }

  unname(element) {
    element.style.removeProperty("view-transition-name")
    element.style.removeProperty("view-transition-class")
    if (!element.getAttribute("style")) element.removeAttribute("style")
  }

  // Les clés des en-têtes de la page courante qui collent à cet instant.
  stuckKeys() {
    const keys = new Set()
    for (const element of this.stickyTargets) {
      const key = element.dataset.continuousPagesPersist
      if (key && this.page?.contains(element) && element.hasAttribute("data-stuck")) keys.add(key)
    }
    return keys
  }

  persistentPairs(from, to) {
    if (!from || !to) return []

    const arrivals = new Map()
    for (const element of to.querySelectorAll(PERSIST)) {
      if (!arrivals.has(element.dataset.continuousPagesPersist)) arrivals.set(element.dataset.continuousPagesPersist, element)
    }

    const pairs = []
    const seen = new Set()
    for (const element of from.querySelectorAll(PERSIST)) {
      const key = element.dataset.continuousPagesPersist
      if (seen.has(key) || !arrivals.has(key)) continue

      seen.add(key)
      pairs.push([element, arrivals.get(key)])
    }
    return pairs
  }

  clearPersisted() {
    for (const { departure, arrival } of this.persisted ?? []) {
      this.unname(departure)
      this.unname(arrival)
    }
    this.persisted = []
  }

  // On arrive en haut de la page suivante, en bas de la précédente : on reprend là où elle
  // touche celle qu'on quitte. Un en-tête collant qui collait sur la page qu'on quitte (`carry` : ses clés, cf.
  // `stuckKeys`) colle aussi sur la suivante : on la défile d'autant, la lecture continue sous le même en-tête, qui ne
  // bouge pas d'un pixel (et sans View Transition, la bascule sèche ne le fait pas non plus sauter). Sans cela, la
  // suivante arriverait en haut, son grand titre déployé.
  place(page, direction, carry) {
    if (direction === "previous") {
      this.scroller.scrollTop = this.scroller.scrollHeight
    } else {
      this.placeTop(page)
      this.stick(page, carry)
    }
    // L'état « collé » se lit sur la page à sa place d'arrivée, dans le même rendu : la capture de la
    // View Transition (et le premier paint) ne montrent jamais celui de la page d'avant.
    this.syncSticky()
  }

  stick(page, keys) {
    if (!keys?.size) return

    for (const element of this.stickyTargets) {
      if (!page.contains(element) || !keys.has(element.dataset.continuousPagesPersist)) continue

      const distance = element.getBoundingClientRect().top - this.scrollportTop + STICK_OVERSHOOT
      if (distance > 0) this.scroller.scrollTop += distance
    }
  }

  // Haut de `element` en haut du conteneur. Si seule la marge du conteneur le précède, on remonte
  // tout en haut ; sur téléphone, en-tête et sommaire le précèdent : on les passe. Une page qui
  // porte un en-tête collant se place à ras : sa marge est dans son grand titre, qui recouvre celle du
  // conteneur (`.continuous-pages__cover`), pour que l'en-tête soit à la même place collé ou non (sinon il
  // glisserait de la marge à la bascule).
  placeTop(element) {
    const scroller = this.scroller
    const viewportTop = scroller === this.documentScroller ? 0 : scroller.getBoundingClientRect().top
    const offset = element.getBoundingClientRect().top - viewportTop + scroller.scrollTop
    const padding = parseFloat(getComputedStyle(scroller).paddingTop) || 0
    const gap = this.stickyTargets.some((sticky) => element.contains(sticky)) ? 0 : this.topGapValue
    scroller.scrollTop = offset - padding <= gap ? 0 : offset - gap
  }

  // ── En-têtes collants ──────────────────────────────────────────────────────────────────────

  // Un en-tête collant (cible `sticky`) porte `data-stuck` dès qu'il colle : sa place naturelle est passée
  // au-dessus du bord haut de ce qui défile, il dépasse alors d'un pixel (`top: -1px`). Mesuré sur la géométrie
  // (le résultat de l'observateur n'est qu'un réveil), donc juste aussi à l'arrivée d'une page, avant que
  // l'observateur ait parlé. Une page cachée n'a pas de boîte : rien n'y colle.
  syncSticky(elements = this.stickyTargets) {
    if (!elements.length) return

    const edge = this.scrollportTop
    for (const element of elements) {
      const stuck = element.getClientRects().length > 0 && element.getBoundingClientRect().top < edge - STUCK_TOLERANCE
      element.toggleAttribute("data-stuck", stuck)
    }
  }

  // Bord haut visible de ce qui défile, dans le repère de la fenêtre.
  get scrollportTop() {
    const scroller = this.scroller
    return scroller === this.documentScroller ? 0 : scroller.getBoundingClientRect().top + scroller.clientTop
  }

  // La page devient courante : seule visible, adresse, titre, lien actif, événement, voisines.
  select(page) {
    this.show(page)
    const { key, url, title } = page.dataset
    this.currentValue = key
    this.highlight(key)

    const location = new URL(url, window.location.origin)
    if (location.pathname !== window.location.pathname) {
      // `history.state` conservé : Turbo y range son identifiant de restauration.
      window.history.replaceState(window.history.state, "", location.pathname + location.search)
    }
    document.title = [title, this.titleSuffix].filter(Boolean).join(" | ")
    this.dispatch("change", { detail: { key, url, title } })
    if (this.prefetchValue) this.prefetchAround(page)
  }

  show(page) {
    this.page = page
    for (const target of this.pageTargets) target.hidden = target !== page
  }

  // ── Voisine pas encore arrivée ─────────────────────────────────────────────────────────────

  // On glisse vers son squelette (le placeholder de sa frame) ; elle le remplacera sur place.
  awaitFrame(frame, direction, carry) {
    const url = frame.getAttribute("src") ?? frame.dataset.lazySrc
    this.pending = { frame, direction, from: this.page, carry }
    this.page.hidden = true
    frame.classList.add("is-pending")
    this.highlightUrl(url)
    if (frame.loading !== "eager") frame.loading = "eager"
    else if (!frame.hasAttribute("busy") && frame.getAttribute("src")) frame.reload()
    this.placeTop(frame)
  }

  // La voisine attendue arrive : visible dès son insertion, placée dans le même rendu.
  beforeFrameRender(event) {
    const pending = this.pending
    if (!pending || event.target !== pending.frame) return

    const render = event.detail.render
    event.detail.render = async (currentElement, newElement) => {
      newElement.querySelector(":scope > .continuous-pages__page")?.removeAttribute("hidden")
      await render(currentElement, newElement)
      this.arrive(pending)
    }
  }

  arrive(pending) {
    if (this.pending !== pending) return

    this.pending = null
    pending.frame.classList.remove("is-pending")
    const page = pending.frame.querySelector(":scope > .continuous-pages__page")
    if (!page) {
      this.show(pending.from)
      this.highlight(pending.from.dataset.key)
      return
    }

    this.select(page)
    this.place(page, pending.direction, pending.carry)
  }

  // Frame en échec (réseau, réponse sans la frame) : on y va par une visite ordinaire, qui
  // affichera la page ou son erreur.
  frameFailed(event) {
    const pending = this.pending
    if (!pending || event.target !== pending.frame) return

    if (event.type === "turbo:frame-missing") event.preventDefault()
    const url = pending.frame.getAttribute("src") ?? pending.frame.dataset.lazySrc
    this.cancelPending()
    if (url) Turbo.visit(url)
  }

  // On quitte l'écran pendant l'attente : l'instantané Turbo garde la page d'où l'on venait.
  cancelPending() {
    const pending = this.pending
    if (!pending) return

    this.pending = null
    pending.frame.classList.remove("is-pending")
    this.show(pending.from)
    this.highlight(pending.from.dataset.key)
  }

  // ── Sommaire ───────────────────────────────────────────────────────────────────────────────

  // Lien du sommaire actif, posé par le serveur au rendu puis tenu à jour ici. Appelable de
  // l'extérieur (clic optimiste) via l'action `continuous-pages#activate`.
  highlight(key) {
    let active
    for (const link of this.linkTargets) {
      const isActive = link.dataset.key === key
      link.classList.toggle(this.activeClassValue, isActive)
      if (isActive) {
        link.setAttribute("aria-current", "page")
        active = link
      } else {
        link.removeAttribute("aria-current")
      }
    }
    if (active) this.revealLink(active)
  }

  // Pendant l'attente d'une voisine, on ne connaît que son adresse.
  highlightUrl(url) {
    if (!url) return

    const pathname = new URL(url, window.location.origin).pathname
    const link = this.linkTargets.find((target) => new URL(target.href, window.location.origin).pathname === pathname)
    if (link) this.highlight(link.dataset.key)
  }

  activate(event) {
    const key = event.currentTarget?.dataset?.key
    if (!key) return

    this.leaving = true
    this.highlight(key)
  }

  // ── Pastilles de l'en-tête commun ──────────────────────────────────────────────────────────

  // Clic sur une pastille (lien de l'en-tête d'une page, `data-action="continuous-pages#pill"`). La visite reste celle de
  // Turbo (adresse, Précédent, Suivant), mais l'en-tête garde son état : à l'instant du clic, ce qui le suit est
  // remplacé par le squelette du <template> (cible `skeleton`), la pastille cliquée devient active, et si la barre
  // collait on revient au point de collage (la page demandée arrive défilée au même endroit : le squelette et la page
  // commencent au même point). Ni titre qui se redéploie, ni barre qui saute. Sans squelette, sans en-tête repérable
  // (`data-continuous-pages-head`), c'est un clic de sommaire ordinaire ; un clic modifié (nouvel onglet) n'est pas touché.
  pill(event) {
    const link = event.currentTarget
    const head = link.closest("[data-continuous-pages-head]")
    const bar = link.closest("[data-continuous-pages-target~='sticky']")
    if (this.isModifiedClick(event)) return

    this.activate(event)
    if (!this.hasSkeletonTarget || !head || !bar || this.previewing) return

    const stuck = bar.hasAttribute("data-stuck")
    this.markPill(head, link)
    this.showSkeleton(head)
    if (stuck) this.scroller.scrollTop += this.naturalTop(bar) - this.scrollportTop + STICK_OVERSHOOT
    carriedHeader = {
      path: new URL(link.href, window.location.origin).pathname, key: bar.dataset.continuousPagesPersist, stuck,
      top: this.scroller.scrollTop, at: performance.now(),
    }
  }

  // La pastille cliquée est la page courante. La précédente est notée : un instantané du cache pris à ce moment la
  // retrouve (`dropSkeleton`).
  markPill(head, link) {
    for (const pill of head.querySelectorAll("[data-action~='continuous-pages#pill']")) {
      if (pill === link) {
        pill.setAttribute("aria-current", "page")
      } else {
        if (pill.hasAttribute("aria-current") && !head.querySelector("[data-continuous-pages-was-current]")) {
          pill.setAttribute("data-continuous-pages-was-current", "")
        }
        pill.removeAttribute("aria-current")
      }
    }
  }

  // Tout ce qui suit l'en-tête dans la page est masqué (pas retiré : il revient si la visite échoue), le squelette en
  // prend la place. Sa hauteur minimale est celle de ce qui défile : sans elle, le navigateur raboterait le défilement
  // d'une barre collée (une page courte ne peut pas défiler de plus que sa hauteur).
  showSkeleton(head) {
    const parent = head.parentElement
    if (parent.querySelector(":scope > [data-continuous-pages-skeleton]")) return

    // Le squelette est posé AVANT de masquer l'ancien contenu, sans relire la mise en page entre les deux : une page
    // sans corps, même un instant, raboterait le défilement.
    const skeleton = document.createElement("div")
    skeleton.setAttribute("data-continuous-pages-skeleton", "")
    skeleton.style.minHeight = `${this.scroller.clientHeight}px`
    skeleton.append(this.skeletonTarget.content.cloneNode(true))
    const held = []
    for (let node = head.nextElementSibling; node; node = node.nextElementSibling) {
      if (!node.hidden) held.push(node)
    }
    parent.append(skeleton)
    for (const node of held) node.setAttribute("data-continuous-pages-held", "")
  }

  // Défait `pill` : la visite a échoué, ou l'on rouvre l'instantané d'une page prise pendant l'attente.
  dropSkeleton() {
    for (const element of this.element.querySelectorAll("[data-continuous-pages-skeleton]")) element.remove()
    for (const element of this.element.querySelectorAll("[data-continuous-pages-held]")) element.removeAttribute("data-continuous-pages-held")
    const previous = this.element.querySelector("[data-continuous-pages-was-current]")
    if (!previous) return

    for (const pill of previous.parentElement.querySelectorAll("[aria-current]")) pill.removeAttribute("aria-current")
    previous.setAttribute("aria-current", "page")
    previous.removeAttribute("data-continuous-pages-was-current")
  }

  // L'en-tête laissé par la page qu'on quitte par une pastille, si cette page est celle qui arrive.
  carriedHeaderFor() {
    const carry = carriedHeader
    if (!carry) return null
    if (performance.now() - carry.at <= HEADER_CARRY_TTL && carry.path === window.location.pathname) return carry

    carriedHeader = null
    return null
  }

  // La page arrive avec l'en-tête là où il était : au point de collage si la barre collait, à la même distance du bord
  // sinon (la mise en page au-dessus de la page est la même d'une sous-page à l'autre). Une page trop courte pour y
  // tenir (le navigateur raboterait le défilement, la barre se décollerait et le titre se redéploierait) est allongée du
  // vide qu'il faut : la barre reste où elle était, la page ne change pas de forme dessous.
  keepHeader(page, carry) {
    const scroller = this.scroller
    const bar = carry.stuck && this.stickyTargets.find((element) => page.contains(element) && element.dataset.continuousPagesPersist === carry.key)
    const target = bar ? this.naturalTop(bar) - this.scrollportTop + scroller.scrollTop + STICK_OVERSHOOT : carry.top
    const box = page.getBoundingClientRect()
    const top = box.top - this.scrollportTop + scroller.scrollTop
    const needed = target + (scroller === this.documentScroller ? window.innerHeight : scroller.clientHeight) - top
    if (needed > box.height) page.style.minHeight = `${Math.ceil(needed)}px`
    scroller.scrollTop = target
  }

  // Le haut d'un élément collant à sa place naturelle (dans le repère de la fenêtre), là où il serait s'il ne collait pas.
  naturalTop(element) {
    const previous = element.style.position
    element.style.position = "static"
    const top = element.getBoundingClientRect().top
    if (previous) element.style.position = previous
    else element.style.removeProperty("position")
    if (!element.getAttribute("style")) element.removeAttribute("style")
    return top
  }

  // Ramène le lien actif dans la partie visible de son conteneur qui défile, sans faire
  // défiler la page (scrollIntoView déplacerait aussi le conteneur principal).
  revealLink(link) {
    const container = this.scrollableAncestor(link)
    if (!container || container === this.scroller) return

    const box = container.getBoundingClientRect()
    const linkBox = link.getBoundingClientRect()
    if (linkBox.top < box.top) container.scrollTop -= box.top - linkBox.top + 16
    else if (linkBox.bottom > box.bottom) container.scrollTop += linkBox.bottom - box.bottom + 16
  }

  scrollableAncestor(element) {
    for (let node = element.parentElement; node && node !== this.element; node = node.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return node
    }
    return null
  }

  // Préchargement silencieux : les frames qui bordent la page courante se chargent tout de
  // suite. Une frame chargée perd son `src` (cf. common.ts) ou porte `complete` : on ne la
  // relance pas.
  prefetchAround(page) {
    for (const sibling of [page.previousElementSibling, page.nextElementSibling]) {
      if (!this.frameTargets.includes(sibling)) continue
      if (!sibling.getAttribute("src") || sibling.hasAttribute("complete") || sibling.hasAttribute("busy")) continue
      if (sibling.loading === "eager") continue

      sibling.loading = "eager"
    }
  }

  isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0
  }
}
